import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment/environment';
import { SKIP_ERROR_TOAST } from '@interceptors/error-toast.interceptor';
import type { PublicCandidateApplicationDTO } from '@libs/dtos/merit/candidate.dto';
import type { CandidateCvPrefillDTO } from '@libs/dtos/merit/cv-extraction.dto';
import type { Observable } from 'rxjs';

/** Candidate form fields (mirrors @libs/dtos CreateCandidateSchema). */
export interface CandidatePayload {
	fullName: string;
	phone: string;
	email: string;
	languages: { language: string; level: string }[];
	availability: string;
	/** Texto libre histórico; se deriva del país y ya no se teclea. */
	nationality: string;
	/** País de nacionalidad en ISO alfa-2 (cascada continente → país). */
	countryCode?: string | null;
	lastPosition: string;
	lastPositionOther?: string | null;
	targetPosition: string;
	targetPositionOther?: string | null;
	yearsOfExperience: string;
	drivingLicence: string;
	salaryExpectation: string;
	visaStatus: string;
	luxuryLevel: string;
	managementExperience: string;
	currentLocation: string;
	currentLocationOtherCountry?: string | null;
	currentLocationOtherCity?: string | null;
	locationTargeting: string[];
	locationTargetingOtherCountry?: string | null;
	locationTargetingOtherCity?: string | null;
	gccExperience: string;
	/** Altura en cm. Informativa: nunca filtra ni descarta. */
	heightCm?: number | null;
	/** Habilidades en texto libre; no entran en ningún baremo. */
	skills?: string[];
	linkedinUrl: string;
	consent?: boolean; // deprecated — no longer collected in the intake form
	source?: string | null;
}

export interface CandidateFiles {
	cv: File[];
	picture: File[];
	video: File[];
}

/** Slice of the admin-edit response the UI reacts to (Fase 3 invite offer). */
export interface CandidateEditResult {
	status: string;
	completionEmailSentAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class CandidateApiService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = `${environment.API_URL}/candidates`;

	/** Public intake: posts the form payload + files as multipart/form-data. */
	submit(payload: CandidatePayload, files: CandidateFiles): Observable<unknown> {
		const formData = new FormData();
		formData.append('payload', JSON.stringify(payload));
		for (const f of files.cv) formData.append('cv', f);
		for (const f of files.picture) formData.append('picture', f);
		for (const f of files.video) formData.append('video', f);
		return this.http.post(this.baseUrl, formData);
	}

	/**
	 * Public: read a CV with AI and get the signup form's proposed values.
	 * `SKIP_ERROR_TOAST`: si la lectura falla, el formulario lo cuenta con su
	 * propia nota discreta — un toast rojo aquí asustaría por algo opcional.
	 */
	cvPrefill(cv: File): Observable<CandidateCvPrefillDTO> {
		const formData = new FormData();
		formData.append('cv', cv);
		return this.http.post<CandidateCvPrefillDTO>(`${this.baseUrl}/cv-prefill`, formData, {
			context: new HttpContext().set(SKIP_ERROR_TOAST, true),
		});
	}

	/**
	 * Admin edit of an existing candidate's form fields (perm `lead-candidate:write`).
	 * Authenticated: the backoffice HTTP interceptor attaches the JWT. Re-scores
	 * server-side. Files are not edited here.
	 */
	update(id: number, payload: Partial<CandidatePayload>): Observable<CandidateEditResult> {
		return this.http.patch<CandidateEditResult>(`${this.baseUrl}/${id}`, payload);
	}

	/**
	 * Admin: (re)send the Fase 3 completion invite email to an approved candidate.
	 * Used when an edit pushes them over the threshold and the admin confirms it.
	 */
	sendCompletionInvite(id: number): Observable<{ sent: boolean }> {
		return this.http.post<{ sent: boolean }>(`${this.baseUrl}/${id}/completion-invite`, {});
	}

	/**
	 * Admin edit: replace one or more of the candidate's files (CV / picture /
	 * video). Only the fields provided are sent; the backend keeps the rest.
	 */
	updateDocuments(id: number, files: Partial<CandidateFiles>): Observable<unknown> {
		const formData = new FormData();
		for (const f of files.cv ?? []) formData.append('cv', f);
		for (const f of files.picture ?? []) formData.append('picture', f);
		for (const f of files.video ?? []) formData.append('video', f);
		return this.http.patch(`${this.baseUrl}/${id}/documents`, formData);
	}

	/** Public (Fase 3): fetch an approved application by its completion token (read-only, no score). */
	getByCompletionToken(token: string): Observable<PublicCandidateApplicationDTO> {
		return this.http.get<PublicCandidateApplicationDTO>(`${this.baseUrl}/complete/${token}`);
	}

	/** Public (Fase 3): upload the presentation video for a completion token. */
	submitCompletionVideo(token: string, video: File): Observable<unknown> {
		const formData = new FormData();
		formData.append('video', video);
		return this.http.post(`${this.baseUrl}/complete/${token}/video`, formData);
	}
}
